/**
 * useSaveProperty — small hook wiring the heart button on property cards
 * to the buyer's saved-properties list. Guards against non-buyer users.
 */
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

let cachedIds = null;
const listeners = new Set();

async function refreshCache() {
    try {
        const r = await api.get("/buyer/saved");
        cachedIds = new Set((r.data.items || []).map((p) => p.id));
    } catch { cachedIds = new Set(); }
    listeners.forEach((f) => f());
}

export function useSaveProperty(propertyId) {
    const { user } = useAuth();
    const nav = useNavigate();
    const [saved, setSaved] = useState(false);
    const [busy, setBusy]   = useState(false);

    useEffect(() => {
        const sync = () => setSaved(!!cachedIds && cachedIds.has(propertyId));
        listeners.add(sync);
        if (user?.role === "buyer") {
            if (!cachedIds) refreshCache().then(sync);
            else sync();
        } else {
            setSaved(false);
        }
        return () => { listeners.delete(sync); };
    }, [propertyId, user]);

    const toggle = async () => {
        if (!user) { toast.info("Sign in to save properties"); nav("/login?role=buyer"); return; }
        if (user.role !== "buyer") { toast.error("Only buyer accounts can save properties"); return; }
        setBusy(true);
        try {
            if (saved) {
                await api.delete(`/buyer/saved/${propertyId}`);
                cachedIds?.delete(propertyId);
                toast.success("Removed from saved");
            } else {
                await api.post("/buyer/saved", { property_id: propertyId });
                cachedIds?.add(propertyId);
                toast.success("Saved to your shortlist");
            }
            listeners.forEach((f) => f());
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return { saved, busy, toggle };
}

export function invalidateSavedCache() { cachedIds = null; listeners.forEach((f) => f()); }
